import { normalize } from '../services/verificationService';

/**
 * 教會名稱正規化與比對（「第一次參與」同名異人判斷用）。
 *
 * getChurchKeys('臺北教會') → ['台北']
 * getChurchKeys('南台中/新營') → ['南台中', '新營']（搬家或兩地聚會）
 * getChurchKeys('265-43' | '' | '慕道者' | '新北市') → []（無可用教會）
 *
 * 比對原則：兩邊都有可用教會且沒有交集才算「不同人」；任一邊沒有可用教會 → 視為相容。
 */

// 常見錯字／異體：ㄧ（注音）→ 一、内 → 內、叫會／就會 → 教會；去掉「真耶穌教會」前綴
const PRE_REPLACEMENTS: Array<[RegExp, string]> = [
  [/ㄧ/g, '一'],
  [/内/g, '內'],
  [/(叫|就)會/g, '教會'],
  [/敎/g, '教'],
  [/真耶穌(教會|教会)?/g, ''],
];

// 縣市前綴（「新北市 林口教會」→ 林口）；臺已先轉台
const ADMIN_PREFIX_RE =
  /^(台北|新北|桃園|台中|台南|高雄|基隆|新竹|嘉義|苗栗|彰化|南投|雲林|屏東|宜蘭|花蓮|台東|澎湖|金門|連江)(市|縣)(\(市\))?/;

// 海外國名前綴（「馬來西亞梳邦教會」→ 梳邦）
const COUNTRY_PREFIX_RE = /^(馬來西亞|新加坡|印尼|菲律賓|香港|澳門|日本|韓國|美國|加拿大|澳洲|紐西蘭)/;

// 教會層級字尾（normalize 已處理「教會」「祈禱所」）
const CHURCH_SUFFIX_RE = /(聚會所|佈道所|布道所|佈道處|禱告所|祈禱所|教會)$/;

// 非教會內容：慕道者、地址、無
const NOT_A_CHURCH_RE = /[慕募]道|[路街號巷弄鄰]|^(無|不詳|未知|其他|教會|none|na|n\/a)$/i;

const CJK_RE = /[㐀-鿿]/;

/**
 * 已知同一間教會的不同寫法（正規化後的 key → 標準 key）。
 * 只放資料上確認過的別名；不同教會（例：台中 vs 北台中）不可合併。
 */
export const CHURCH_ALIASES: Record<string, string> = {
  // 總名冊「疏邦」＝報名表「馬來西亞梳邦教會」（同一人兩處寫法，見 reports/church-matching）
  疏邦: '梳邦',
};

const SPLIT_RE = /[/／、,，;；&＆|+＋（）()]|\s+or\s+/i;

const normalizeChurchPart = (part: string): string => {
  let value = normalize(part);
  // normalize 已把「臺」轉「台」、去掉空白、「教會」「祈禱所」字尾與「真耶穌」
  value = value.replace(/[0-9a-z\-_.#:：]/gi, '');
  value = value.replace(ADMIN_PREFIX_RE, '');
  const withoutCountry = value.replace(COUNTRY_PREFIX_RE, '');
  if (withoutCountry.length >= 2) value = withoutCountry;
  value = value.replace(CHURCH_SUFFIX_RE, '');
  if (!value || !CJK_RE.test(value)) return '';
  if (NOT_A_CHURCH_RE.test(value)) return '';
  // 單字（「外教會」→ 外）不當作教會；縣市名已在 ADMIN_PREFIX_RE 剝除（「台北市」→ 空）
  if (value.length < 2) return '';
  return CHURCH_ALIASES[value] || value;
};

/** 教會字串（或多個）→ 正規化後的教會 key 集合；無可用教會回傳 []。 */
export const getChurchKeys = (raw: string | string[] | null | undefined): string[] => {
  const inputs = Array.isArray(raw) ? raw : [raw];
  const keys = new Set<string>();
  inputs.forEach(input => {
    if (input === null || input === undefined) return;
    let text = String(input).trim();
    if (!text) return;
    PRE_REPLACEMENTS.forEach(([pattern, replacement]) => {
      text = text.replace(pattern, replacement);
    });
    text.split(SPLIT_RE).forEach(part => {
      const key = normalizeChurchPart(part || '');
      if (key) keys.add(key);
    });
  });
  return Array.from(keys);
};

/** 單一教會字串是否可用（非空白／郵遞區號／地址／慕道者）。 */
export const hasUsableChurch = (raw: string | string[] | null | undefined): boolean =>
  getChurchKeys(raw).length > 0;

/** 任一邊沒有可用教會 → 相容；兩邊都有 → 需有交集。 */
export const churchKeysCompatible = (left: string[], right: string[]): boolean => {
  if (left.length === 0 || right.length === 0) return true;
  const rightSet = new Set(right);
  return left.some(key => rightSet.has(key));
};

/**
 * 紀錄的教會 key：有 `churches`（補充後的集合）就用它，否則退回 `church`。
 * `churches: []` 代表已確認原 church 欄位不可用（例：誤植的姓名），視為無教會。
 */
export const getRecordChurchKeys = (record: { church?: unknown; churches?: unknown }): string[] => {
  if (Array.isArray(record?.churches)) {
    return getChurchKeys(record.churches.map(value => String(value ?? '')));
  }
  const church = record?.church;
  return typeof church === 'string' || typeof church === 'number' ? getChurchKeys(String(church)) : [];
};
